import React, { Suspense, lazy, useMemo } from "react";
import { PaymentReturnPage } from "./pages/PaymentReturnPage";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useMatch, useNavigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { SuiteAppShell, initSuiteThemeFromStorage, initSuiteLocaleFromStorage, fetchSuiteAppsFromApi, readEffectiveTheme, subscribeEffectiveTheme, userCanAccessAdminApp, type SidebarGroup } from "@weco/suite-chrome";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import "@weco/ui/tokens.css";
import { routes, sidebarSecondary, sidebarSections, bottomNavRoutes, getRouteById, type Route as AppRoute } from "./routes";
import { ComingSoon } from "./pages/ComingSoon";
import { ModuleAccessGuard } from "./components/ModuleAccessGuard";
import { AppAccessGuard } from "./components/AppAccessGuard";
import { canAccessModule, readModuleAccess } from "./lib/moduleAccess";
import { isAppEnabled } from "./lib/appAccess";
import { useEnabledApps } from "./hooks/useEnabledApps";
import { resolveDefaultLanding } from "./lib/resolveDefaultLanding";
import { LandingRedirect } from "./components/LandingRedirect";
import { HeaderTabs, type HeaderTabMeta } from "./shell/HeaderTabs";
import { WorkStatusBar } from "./shell/WorkStatusBar";
import { HeaderCheckOutButton } from "./shell/HeaderCheckOutButton";
import { SessionReconnectingBanner } from "./shell/SessionReconnectingBanner";
import { useShellTabs } from "./shell/tabs/useShellTabs";
import { DOC_ROUTE_IDS, SHELL_PAGE_IDS, resolveTabTarget, type ShellTab, type TabRouteRef } from "./shell/tabs/tabModel";
import { SHELL_PAGE_META } from "./shell/shellPages";

import LoginScreen from "./components/LoginScreen";
import { AccountPage } from "./pages/AccountPage";
import { GuestMeetingPage } from "./pages/GuestMeetingPage";
const PublicDeckPage = lazy(() => import("./pages/PublicDeckPage"));
const RequestFormPage = lazy(() => import("./pages/RequestFormPage"));
const OnboardingAdminApp = lazy(() => import("./modules/onboarding/admin/OnboardingAdminApp"));
import { PreferencesPage } from "./pages/PreferencesPage";
import { ProfilePage } from "./pages/ProfilePage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { AcceptInvitationPage } from "./pages/AcceptInvitationPage";
import { SuiteSystemStatusPage } from "./pages/SuiteSystemStatusPage";
import { setRefreshHandler } from "@weco/suite-client";
import { useShellStore } from "./store";
import { queryClient } from "./lib/queryClient";
import { useTenantBranding } from "./hooks/useTenantBranding";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { CallSessionRoom } from "./modules/call/components/CallSessionRoom";
import { ProductTour } from "./components/ProductTour";
import { BillingBanner } from "./components/billing/BillingBanner";
import { BillingAccessGate } from "./components/billing/WorkspacePausedPage";
import { BILLING_STATUS_QUERY_KEY, installWorkspacePausedHandler } from "./lib/billing";
import { MochiCreateHost } from "./components/mochi/create";
import { IncomingCallBanner } from "./modules/call/components/IncomingCallBanner";
import { SourceUpdatesWatcher } from "./modules/tasks/capture/SourceUpdatesWatcher";
import { TopbarTimer } from "./modules/tasks/reporting/time";
import { TasksNotifySync } from "./modules/tasks/inbox/notifySync";
import { useInboxCounts } from "./modules/tasks/inbox/api";
import { NotificationsBridge, notificationPanelLabels } from "./lib/notifications";
import { DesktopOpenFilesBridge } from "./lib/desktop/DesktopOpenFilesBridge";
import { useIsRinging } from "./modules/call/store/incomingRingStore";
// Mounted for the whole shell, not inside HomeWork: presence, and being asked
// to talk, have to survive walking over to another module. The socket stays
// shut until this tab has opened HomeWork — see lib/officeSession.
import { OfficeRealtimeProvider } from "./modules/homework/realtime/OfficeRealtimeProvider";
import { KnockPrompts } from "./modules/homework/components/KnockPrompts";
import { KnockResolutions } from "./modules/homework/components/KnockResolutions";
import { RoomEntryPrompts } from "./modules/homework/components/RoomEntryPrompts";
import { AssistantConversationProvider } from "./modules/assistant/state/AssistantConversationProvider";
import { MochiOverlay } from "./components/mochi/MochiOverlay";
import { useAssistantConfig } from "./components/mochi/useAssistantConfig";
import { MochiNavIcon } from "./components/mochi/MochiNavIcon";
import { MOCHI_SYSTEM_DEFAULTS } from "./components/mochi/avatars";
import { installCallAudioPrimer } from "./modules/call/lib/callAudio";
import { ttsPlayer } from "./modules/assistant/lib/ttsPlayer";
// Temporarily hidden: the GlobalAssistant floating button overlapped content on
// mobile. Re-enable by uncommenting this import and its <GlobalAssistant /> mount below.
// import { GlobalAssistant } from "./modules/assistant/components/GlobalAssistant";
import "./index.css";
import { i18nReady } from "./i18n/index";
import { applyLocale } from "./i18n/applyLocale";

initSuiteThemeFromStorage();
initSuiteLocaleFromStorage();
// Apply dir/lang on first load to match stored locale
applyLocale((localStorage.getItem("weco-locale") as "en" | "ar") ?? "en");

/** Route table the header tabs resolve URLs against (stable identity). */
const TAB_ROUTES: ReadonlyArray<TabRouteRef> = routes.map((r) => ({ id: r.id, path: r.path }));

/** English fallbacks for the sidebar section headings until the keys land. */
const SECTION_FALLBACK_LABELS: Record<string, string> = {
  essentials: "Essentials",
  communication: "Communication",
  files: "Files",
  workspace: "Workspace",
};

const Shell: React.FC = () => {
  const user = useShellStore((s) => s.user);
  const logout = useShellStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const branding = useTenantBranding();
  const { t, i18n } = useTranslation();

  // RBAC module gating (#1342): hide modules the user's role doesn't include.
  // Fail-open until the org is flagged into RBAC (rbac_enabled JWT claim).
  // Re-evaluated per render; token rotations re-render via the user object.
  const moduleAccess = readModuleAccess();
  // Org-level app access (Per-org app access / App Registry screens): hide
  // apps disabled for this org or globally. Fail-open until /config/apps/
  // loads. Applies to super admins too — the Admin module itself is never
  // gated (core app, unmapped in ROUTE_APP_IDS), so re-enabling stays
  // reachable.
  const enabledAppIds = useEnabledApps();
  const defaultLanding = resolveDefaultLanding(enabledAppIds);
  const routeVisible = (r: AppRoute) =>
    canAccessModule(moduleAccess, r.moduleScope) &&
    isAppEnabled(enabledAppIds, r.id);
  // The "dashboard" route is the Mochi/Intelligence-Hub landing; its sidebar +
  // tab label follows the user's chosen assistant name (defaulting to "Mochi")
  // rather than the static "Intelligence Hub" nav string.
  const configuredAssistantName = useAssistantConfig().assistant_name;
  // In Arabic the untouched default ("Mochi") reads as an English brand in an
  // otherwise Arabic sidebar, so it gives way to nav.dashboard. A name the
  // user or org actually chose is shown as-is in every language.
  const assistantName =
    i18n.language?.startsWith("ar") &&
    configuredAssistantName === MOCHI_SYSTEM_DEFAULTS.assistant_name
      ? t("nav.dashboard")
      : configuredAssistantName;
  // Sidebar labels come from nav.<routeId>; the hardcoded English label is
  // only a fallback so a future route without a key degrades gracefully
  // (src/i18n/navKeys.test.ts still fails loudly in CI).
  const localizeNav = (rs: ReadonlyArray<AppRoute>): AppRoute[] =>
    rs.map((r) => ({
      ...r,
      label: r.id === "dashboard" ? assistantName : t(`nav.${r.id}`, { defaultValue: r.label }),
      // Mochi's entry shows the person's own companion, in its chosen colour.
      ...(r.id === "dashboard" ? { icon: MochiNavIcon } : {}),
    }));
  const visibleGroups: SidebarGroup[] = sidebarSections.map((section) => ({
    id: section.id,
    label: t(section.labelKey, { defaultValue: SECTION_FALLBACK_LABELS[section.id] ?? section.id }),
    routes: localizeNav(section.routes.filter(routeVisible)),
  }));
  const visibleSecondary = localizeNav(sidebarSecondary.filter(routeVisible));
  const visibleBottomNav = localizeNav(bottomNavRoutes.filter(routeVisible)).map((r) =>
    r.id === "dashboard" ? { ...r, shortLabel: t("shell.bottomNav.hub", { defaultValue: "Hub" }) } : r,
  );

  // A tab may be opened (or restored) only for something the user can open:
  // the same app-access + RBAC filter as the sidebar, plus the admin gate.
  const isTabAllowed = (routeId: string): boolean => {
    if (SHELL_PAGE_IDS.has(routeId)) return true;
    const r = getRouteById(routeId);
    if (!r) return false;
    if (r.requiresSuperAdmin && !userCanAccessAdminApp(user ?? null)) return false;
    return routeVisible(r);
  };
  const shellTabs = useShellTabs({
    routes: TAB_ROUTES,
    userId: user?.id,
    isAllowed: isTabAllowed,
    homeUrl: defaultLanding,
  });
  const activeTarget = resolveTabTarget(location.pathname, TAB_ROUTES);
  const activeRouteId = activeTarget?.routeId ?? null;
  const tabMeta = (tab: ShellTab): HeaderTabMeta => {
    const shellPage = SHELL_PAGE_META[tab.routeId];
    const route = getRouteById(tab.routeId);
    const moduleLabel = shellPage
      ? t(shellPage.labelKey, { defaultValue: shellPage.fallback })
      : tab.routeId === "dashboard"
        ? assistantName
        : t(`nav.${tab.routeId}`, { defaultValue: route?.label ?? tab.routeId });
    const docTitle = DOC_ROUTE_IDS.has(tab.routeId) && tab.docId ? tab.title : undefined;
    return {
      label: docTitle || moduleLabel,
      Icon: shellPage?.icon ?? route?.icon ?? Sparkles,
      isHub: tab.routeId === "dashboard",
      isDocTitle: Boolean(docTitle),
    };
  };
  // Focused editors (a Write/Present/Grid document) and Call own the bottom
  // edge with their own toolbars.
  const hideBottomNav = (pathname: string) => {
    const target = resolveTabTarget(pathname, TAB_ROUTES);
    return Boolean(target && (target.routeId === "call" || (DOC_ROUTE_IDS.has(target.routeId) && target.docId)));
  };
  const homeworkRoute = getRouteById("homework");
  const homeworkVisible = Boolean(homeworkRoute && routeVisible(homeworkRoute));

  // Keep i18next in sync when the locale toggle fires (same-tab custom event)
  React.useEffect(() => {
    const onLocaleUpdated = () => {
      const next = (localStorage.getItem("weco-locale") as "en" | "ar") ?? "en";
      void i18n.changeLanguage(next);
      applyLocale(next);
    };
    window.addEventListener("weco-locale-updated", onLocaleUpdated);
    return () => window.removeEventListener("weco-locale-updated", onLocaleUpdated);
  }, [i18n]);

  const isRinging = useIsRinging();
  // US 10.1 — the Tasks shortcut shows the Tasks Inbox unread count.
  const tasksUnread = useInboxCounts().data?.unread ?? 0;
  const sidebarBadges = useMemo(() => ({ tasks: tasksUnread }), [tasksUnread]);

  return (
    <OfficeRealtimeProvider>
    <AssistantConversationProvider>
      <SuiteAppShell
        mode="shell"
        shellUser={user}
        onShellSignOut={() => void logout()}
        onLogoClick={() => navigate(defaultLanding)}
        onProfileClick={() => navigate("/profile")}
        loadSuiteApps={() => fetchSuiteAppsFromApi(() => useShellStore.getState().getValidAccessToken())}
        suiteAppsReloadKey={user?.id ?? ""}
        brandingLogoUrl={branding.logo_url || undefined}
        brandingAppName={branding.app_name || undefined}
        sidebarGroups={visibleGroups}
        sidebarSecondary={visibleSecondary}
        activeRouteId={activeRouteId}
        bottomNavItems={visibleBottomNav}
        hideBottomNav={hideBottomNav}
        ringingRouteId={isRinging ? "call" : null}
        sidebarBadges={sidebarBadges}
        headerTabs={
          <HeaderTabs
            tabs={shellTabs.tabs}
            activeKey={shellTabs.activeKey}
            onSelect={shellTabs.select}
            onClose={shellTabs.close}
            meta={tabMeta}
            ariaLabel={t("shell.tabs.ariaLabel", { defaultValue: "Open activities" })}
            closeLabel={t("shell.tabs.close", { defaultValue: "Close tab" })}
          />
        }
        renderHeaderStatus={({ compact }) => (
          <>
            <SessionReconnectingBanner compact={compact} />
            {/* Design (weco2/shell.jsx): Check out sits just before the work
                status while you are checked in to HomeWork. */}
            <HeaderCheckOutButton compact={compact} enabled={homeworkVisible} />
            <WorkStatusBar compact={compact} enabled={homeworkVisible} />
          </>
        )}
        searchPlaceholder={t("search.placeholder")}
        topbarLabels={{
          searchAriaLabel: t("search.ariaLabel"),
          notificationsLabel: t("notifications.label"),
          notificationsEmptyLabel: t("notifications.empty"),
          notifications: notificationPanelLabels(t),
          mobileSearchAriaLabel: t("search.mobileAriaLabel"),
          openMenuLabel: t("sidebar.openMenu"),
          closeMenuLabel: t("sidebar.closeMenu"),
          userMenuLabel: t("shell.userMenu.label", { defaultValue: "Open your profile menu" }),
          viewProfileLabel: t("shell.userMenu.viewProfile", { defaultValue: "View profile" }),
          languageLabel: t("shell.userMenu.language", { defaultValue: "Language" }),
          appearanceLabel: t("shell.userMenu.appearance", { defaultValue: "Appearance" }),
          toArabicLabel: t("locale.toArabic"),
          toEnglishLabel: t("locale.toEnglish"),
          toDarkLabel: t("theme.toDark"),
          toLightLabel: t("theme.toLight"),
          logOutLabel: t("shell.userMenu.logOut", { defaultValue: "Log out" }),
          loggingOutLabel: t("shell.userMenu.loggingOut", { defaultValue: "Logging out…" }),
        }}
        sidebarLabels={{
          expandSidebar: t("sidebar.expand"),
          collapseSidebar: t("sidebar.collapse"),
          homeLabel: t("shell.home", { defaultValue: "Home" }),
          navigationLabel: t("shell.primaryNavigation", { defaultValue: "Primary navigation" }),
        }}
        mobileNavLabels={{
          appsTitle: t("shell.apps.title", { defaultValue: "Apps" }),
          appsEyebrow: t("shell.apps.eyebrow", { defaultValue: "APPS" }),
          moreLabel: t("shell.bottomNav.more", { defaultValue: "More" }),
          navigationLabel: t("shell.primaryNavigation", { defaultValue: "Primary navigation" }),
        }}
      >
      {/* Owners/Admins only: a ready, overdue or trial-ending bill. */}
      <BillingBanner />
      {/* Same box as the content panel, so modules sized to it are unchanged
          whether or not the banner above takes a strip. */}
      <div className="flex-1 min-h-0 min-w-0 overflow-hidden flex flex-col relative w-full">
      <Suspense fallback={<div className="flex-1 flex items-center justify-center text-muted-foreground">{t("shell.loading")}</div>}>
        {/*
          Key by the first path segment, not the full pathname. The full-path
          key force-remounts the whole module subtree on every URL change,
          which wipes local module state — e.g. EmailModule's deep-link
          effect queues setSelectedThreadId(...) + navigate("/email"), the
          pathname changes, the boundary remounts, and the queued state is
          lost. Keying by /<module> preserves within-module navigation while
          still resetting the error boundary when the user switches modules.
        */}
        <RouteErrorBoundary key={location.pathname.split("/")[1] || "/"}>
          <Routes>
            {routes.map((r) => {
              if (r.kind === "component") {
                const C = r.Component;
                const element = (
                  <ModuleAccessGuard moduleScope={r.moduleScope}>
                    <AppAccessGuard routeId={r.id}>
                      <C />
                    </AppAccessGuard>
                  </ModuleAccessGuard>
                );
                return (
                  <Route
                    key={r.id}
                    path={r.path === "/" ? "/*" : `${r.path}/*`}
                    element={r.id === "dashboard" ? <LandingRedirect>{element}</LandingRedirect> : element}
                  />
                );
              }
              return <Route key={r.id} path={`${r.path}/*`} element={<ComingSoon />} />;
            })}

            {/* Pre-existing shell-only routes (account, profile, etc.) */}
            <Route path="/account" element={<AccountPage />} />
            <Route path="/preferences" element={<PreferencesPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/suite-status" element={<SuiteSystemStatusPage />} />

            <Route path="*" element={<Navigate to={defaultLanding} replace />} />
          </Routes>
        </RouteErrorBoundary>
      </Suspense>
      </div>
    </SuiteAppShell>
    <CallSessionRoom />
    {/* Authenticated-only: guests have no JWT `sub` to be rung on. */}
    <IncomingCallBanner />
    {/* US 12.7 — follow up a task's email or chat after I change it. */}
    <SourceUpdatesWatcher />
    {/* US 14.1 — a running task timer shows in the top bar. */}
    <TopbarTimer />
    {/* US 10.1 / 10.3 — the bell and the Tasks Inbox read as one. */}
    <TasksNotifySync />
    <NotificationsBridge />
    <DesktopOpenFilesBridge />
    <ProductTour />
    <KnockPrompts />
    <KnockResolutions />
    <RoomEntryPrompts />
    <MochiOverlay />
    <MochiCreateHost />
    {/* Temporarily hidden — floating assistant button overlapped content on mobile.
        Uncomment (and its import above) to re-enable. */}
    {/* <GlobalAssistant /> */}
    </AssistantConversationProvider>
    </OfficeRealtimeProvider>
  );
};

/**
 * Minimal scaffold for anonymous visitors on /call/<uuid>. Mounts the same
 * <Routes /> tree as <Shell /> so RoomRoute can render, but skips
 * <SuiteAppShell /> (topbar, sidebar, onboarding modal, tenant branding
 * fetch) — none of that is meaningful for a guest who lacks an account.
 * Skipping it also sidesteps an upstream re-render loop in
 * useGlobalSearch's unstable modules ref when the topbar mounts without
 * an authenticated session.
 */
const GuestShell: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  return (
    <>
      <Suspense
        fallback={
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            {t("shell.loading")}
          </div>
        }
      >
        <RouteErrorBoundary key={location.pathname.split("/")[1] || "/"}>
          <Routes>
            {routes.map((r) => {
              if (r.kind === "component") {
                const C = r.Component;
                return (
                  <Route
                    key={r.id}
                    path={r.path === "/" ? "/*" : `${r.path}/*`}
                    element={<C />}
                  />
                );
              }
              return (
                <Route
                  key={r.id}
                  path={`${r.path}/*`}
                  element={<ComingSoon />}
                />
              );
            })}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </RouteErrorBoundary>
      </Suspense>
      {/* Mirror Shell's LiveKitRoom so guest Join() — which writes to the
          same useCallSessionStore — actually opens a LiveKit connection
          and lets CallRouteAwareRenderer portal <InCallView> into the
          target div RoomRoute publishes. Without this, a guest who
          clicks Join lands on an empty bg-background div (the portal
          target with nothing portaled into it). Shared wrapper so guests
          get the same bounded auto-rejoin as authed users (#1178). */}
      <CallSessionRoom />
    </>
  );
};

const Root: React.FC = () => {
  const { isAuthenticated } = useShellStore();
  const location = useLocation();
  const guestMatch = useMatch("/room/:joinCode");
  // /call/:slug is allowed to render for unauthenticated visitors so that
  // rooms with allow_guests=true can serve a guest PreJoin flow.
  // Resolution: permissive wrap — let the route mount; RoomRoute itself calls
  // redirectToShellHome() when the backend returns 401/403 (non-opted-in
  // room hit by an anonymous visitor). Other module routes are unaffected.
  const callRoomMatch = useMatch("/call/:slug");
  // /call/r/:joinCode is the guest-link hand-off target: GuestMeetingPage
  // mints a token into sessionStorage, then full-page-navigates here for
  // GuestRoomRoute to pick up. It is two segments, so useMatch("/call/:slug")
  // above can never match it — without this second match an unauthenticated
  // guest falls through to LoginScreen and the guest link is dead end-to-end
  // (GH-1382 AC6: backend minted a valid token, but the visitor never reached
  // LiveKit — no participant_joined ever fired for them).
  const guestRoomMatch = useMatch("/call/r/:joinCode");
  // /p/:token — a published, view-only presentation. Rendered outside the
  // shell for everyone (signed in or not): it never needs an account.
  const publicDeckMatch = useMatch("/p/:token");
  // /forms/:token — a Squad's request form (US 12.8). Outside the shell for
  // everyone: public forms need no account; the page itself asks a visitor to
  // sign in when the form is for the workspace only.
  const requestFormMatch = useMatch("/forms/:token");

  if (requestFormMatch) {
    return (
      <Suspense fallback={null}>
        <RequestFormPage token={requestFormMatch.params.token ?? ""} />
      </Suspense>
    );
  }

  if (publicDeckMatch) {
    return (
      <Suspense fallback={null}>
        <PublicDeckPage token={publicDeckMatch.params.token ?? ""} />
      </Suspense>
    );
  }

  if (guestMatch) {
    return <GuestMeetingPage joinCode={guestMatch.params.joinCode ?? ""} />;
  }

  if ((callRoomMatch || guestRoomMatch) && !isAuthenticated) {
    // Guest visitor on an allow_guests=true room. Render a minimal scaffold
    // (routes + RouteErrorBoundary + Suspense) without the full SuiteAppShell
    // — no topbar / sidebar / onboarding modal / tenant branding. RoomRoute
    // calls redirectToShellHome() when the backend returns 401/403, so
    // non-opted-in rooms still bounce to sign-in.
    return <GuestShell />;
  }

  // The invitation flow signs the invitee in part-way through (2FA enrolment
  // and the module tour need the new session), so it must stay mounted once
  // isAuthenticated flips — rendered here whether or not a session exists.
  if (location.pathname === "/accept-invitation") {
    return <AcceptInvitationPage />;
  }

  if (!isAuthenticated) {
    if (location.pathname === "/reset-password") {
      return <ResetPasswordPage />;
    }
    // Returning from checkout with an expired session: sign in, then the
    // billing page shows whatever the callback recorded.
    if (location.pathname === "/billing/return") {
      return <LoginScreen />;
    }
    return <LoginScreen />;
  }

  // A workspace paused for an unpaid bill gets the billing-only page and
  // nothing else — at every path, /billing/return included, where that page
  // waits for the payment to be confirmed.
  return <BillingAccessGate>{renderAuthenticated(location.pathname)}</BillingAccessGate>;
};

function renderAuthenticated(pathname: string): React.ReactNode {
  // Workspace onboarding for an org owner (Workspace ready → setup wizard →
  // Setup complete): full-screen, outside the shell chrome, but only for a
  // signed-in user — a signed-out visitor gets LoginScreen above and comes
  // back to the same URL once signed in.
  if (pathname === "/onboarding" || pathname.startsWith("/onboarding/")) {
    return (
      <Suspense fallback={null}>
        <OnboardingAdminApp />
      </Suspense>
    );
  }

  // Paymob sends the browser here after checkout. Handled before <Shell /> so
  // it renders without the sidebar the customer was not looking at anyway.
  if (pathname === "/billing/return") {
    return <PaymentReturnPage />;
  }

  return <Shell />;
}

// Route all token refreshes through the shell store's single-flight dedup so
// suite-client and the shell never fire parallel requests with the same token.
setRefreshHandler(() => useShellStore.getState().refreshAccessToken());

// One place decides what a `403 workspace_paused` from any API means: renew
// the session and let People answer — a member is refused (signed out, and
// LoginScreen says the workspace is paused), an Owner/Admin gets a
// billing_only token and the billing-only page.
installWorkspacePausedHandler({
  isAuthenticated: () => useShellStore.getState().isAuthenticated,
  refresh: () => useShellStore.getState().refreshAccessToken(),
  invalidateStatus: () => void queryClient.invalidateQueries({ queryKey: BILLING_STATUS_QUERY_KEY }),
});

// GH-1387 (D2): fetch + decode the ringback/ringtone files and clear the
// autoplay gate on the user's first interaction, whatever it is. Ringing is
// triggered by an arriving invite rather than by a click, so the permission has
// to be banked ahead of time or the first `play()` is rejected.
installCallAudioPrimer();
ttsPlayer.installAutoplayPrimer();

// Gate the first render on i18n readiness: init is synchronous today
// (bundled resources), but this guarantees no untranslated first paint if
// translations ever move to lazy chunks or an HTTP backend.
/**
 * Toasts follow the suite's theme.
 *
 * Sonner's `theme` prop defaults to `"light"`, so every toast in the app —
 * HomeWork's knock and room-entry prompts included — rendered light chrome on
 * a dark page. `"system"` would only trade that for following the operating
 * system instead of the theme the user actually picked, so this reads the
 * class the topbar toggles and re-reads it when it changes.
 */
function ThemedToaster() {
  const [theme, setTheme] = React.useState(readEffectiveTheme);
  React.useEffect(() => subscribeEffectiveTheme(setTheme), []);
  return <Toaster position="bottom-right" richColors closeButton theme={theme} />;
}

void i18nReady.then(() => {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Root />
          <ThemedToaster />
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  );
});

// GH-883 (AC6): register the offline app-shell service worker. Production only —
// in dev it would fight Vite's HMR/module graph. Scope "/" so it covers the
// Write module routes (/write/*). Registration failure is non-fatal; the app
// works online without it.
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/service-worker.js").catch(() => {
      /* offline support unavailable — app still works online */
    });
  });
}
